/**
 * Compact icon renderer for delivery modes (Iter 97.2 — REEDOS review feedback).
 *
 * The Commercial Structure and Scope sections of the proposal now use small
 * Phosphor icons (no labels) instead of long delivery-mode text strings. The
 * iconName comes from DELIVERY_MODES in builderStorage.js — single source of
 * truth so adding/removing a mode automatically flows through every render.
 */

import { Globe, AndroidLogo, AppleLogo, Code, Broadcast } from "@phosphor-icons/react";
import { DELIVERY_MODES } from "./builderStorage";

const ICON_BY_NAME = {
    globe: Globe,
    android: AndroidLogo,
    apple: AppleLogo,
    code: Code,
    broadcast: Broadcast,
};

export function DeliveryIcon({ iconName, label, size = 14, weight = "duotone", className = "" }) {
    const I = ICON_BY_NAME[iconName];
    if (!I) return null;
    return <I size={size} weight={weight} className={className} aria-label={label} title={label} />;
}

/**
 * Render the set of delivery icons for a product's selected mode codes. Returns
 * `null` if no modes are selected so callers can suppress the wrapping element.
 */
export function DeliveryIconStrip({ codes = [], size = 14, gap = "gap-1", testidPrefix }) {
    if (!Array.isArray(codes) || codes.length === 0) return null;
    const modeByCode = Object.fromEntries(DELIVERY_MODES.map((m) => [m.code, m]));
    const visible = codes.map((c) => modeByCode[c]).filter(Boolean);
    if (visible.length === 0) return null;
    return (
        <span className={`inline-flex items-center ${gap} align-middle text-slate-600`} data-testid={testidPrefix ? `${testidPrefix}-icons` : undefined}>
            {visible.map((m) => (
                <DeliveryIcon key={m.code} iconName={m.iconName} label={m.label} size={size} />
            ))}
        </span>
    );
}
